import React, { useContext, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components/native';
import { View, Text, TouchableOpacity } from 'react-native';
import { FlexBlockWrapped, FlexBlock, Loading, StrongText, Heading3, MetricStackTitle, ShinyBlockOuter, ShinyBody, ShinyHeading, Button, BasicText, SmallButton, PageWrapper } from '../Styles';
import { MetricStack } from '../TestingLayout';
import { Tabular, TabularColumn } from '../core/Tabular';
import _ from 'lodash';
import BarChart from '../core/BarChart';
import WasteBrand, { COLORS } from '../../WasteTheme';
import Metrics from '../core/Metrics';
import { CircleChart, DiversionHistogram, niceRound } from '../Things';
import OpidLink from '../core/OpidLink';
import * as scale from 'd3-scale';
import { useOpidNavigator } from '../core/OpidNavigator';
import { SiteRankingTabular } from '../Site/SiteComparison';
import Icon from '../core/Icon';
import { Contributors } from '../Sustainability/SustainabilityDashboard';
import Util from '../core/Util';

const SummaryDashboard = ({portalData}) =>{
    const [metricData, setMetricData] = useState(null);
    const navigation = useOpidNavigator();
    
    useEffect(()=>{
        if(portalData.metrics != undefined){
            let filterOutValue = "Cost Savings";

            if(portalData.isSharedSavings == true){
                filterOutValue = "Cost Avoidance";
            }

            const newData = _.orderBy(_.filter(portalData.metrics["summary"], (row)=>row.title != filterOutValue), "periodLength", "desc");
            
            setMetricData(_.groupBy(newData, "category"));
        }
    },[portalData]);

    if (portalData.materials == null){
        return null;
    }

    const materialData = {};

    _.forEach(portalData.materials, (m)=>{
        if(materialData[m.name] == undefined){
            materialData[m.name] = {...m};
        } else {
            materialData[m.name].metric_tonnes = materialData[m.name].metric_tonnes + m.metric_tonnes;
            materialData[m.name].date = m.date;
        }
    });

    const groupedMaterials = _.values(materialData);

    console.log("showing metrics");
    console.log(metricData);
    return <PageWrapper>
        

        <Metrics data={metricData}>
            <JumpToPageButton/>
        </Metrics>

   
        <ShinyBlockOuter flexBasis={Util.isMobile()? "95%" : "49%"}>
            <ShinyHeading>
                <MetricStackTitle>{"Historical Diversion"}</MetricStackTitle>
            </ShinyHeading>
            <ShinyBody>
                <DiversionHistogram data={ portalData.diversion } defaultDataPoint={portalData.diversion[portalData.diversion.length-1]} maxValue={20} portalData={portalData}/>                        
            </ShinyBody>
        </ShinyBlockOuter>
    
        <ShinyBlockOuter flexBasis={Util.isMobile()? "95%" : "49%"}>
            <ShinyHeading>
                <MetricStackTitle>Landfill Diversion ({portalData.dateRange.length || 0} Months)</MetricStackTitle>
            </ShinyHeading>
            <ShinyBody>
                <DiversionSummary data={groupedMaterials} portalData={portalData}/>
            </ShinyBody>
        </ShinyBlockOuter>
        <Contributors portalData={portalData} title="Material Composition"/>
    
        <DiversionBlock portalData={portalData} diversionData={portalData.adjustedSiteData} navigation={navigation}/>

    </PageWrapper>
};

const DiversionBlock = ({portalData, diversionData, navigation}) => {
    return <ShinyBlockOuter flexBasis={Util.isMobile()? "95%" : "49%"}>
        <ShinyHeading>
            <MetricStackTitle>{"Site Ranking"} ({portalData.dateRange.length || 0} Months)</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <SiteRankingTabular rawData={diversionData} topRanking={5} rowHeight={60}/>

            {diversionData.length <= 1? null : <SmallButton onPress={()=>navigation.goto("/site/comparison")} theme={WasteBrand} style={{marginTop: 10}}><BasicText colour="white">Site Comparison</BasicText><Icon icon={"chevron-right"} colour="white" size={14}/></SmallButton>}
        </ShinyBody>
    </ShinyBlockOuter>;
}


const JumpToPageButton = ({categoryName = undefined})=>{
    const navigation = useOpidNavigator();

    if (categoryName == "finance"){
        return <SmallButton onPress={()=>navigation.goto("/dashboard/finance")} theme={WasteBrand}><BasicText colour="white">Finance</BasicText><Icon icon={"chevron-right"} colour="white" size={14}/></SmallButton>
    }

    if (categoryName == "environment"){
        return <SmallButton onPress={()=>navigation.goto("/carbonImpact")} theme={WasteBrand}><BasicText colour="white">Sustainability</BasicText><Icon icon={"chevron-right"} colour="white" size={14}/></SmallButton>
    }

    return null;
}
export const MaterialGraph = ({data, units})=>{
    const maxValue = useRef(0);
	const isUS = window.PORTAL_COUNTRY == "USA";
    
    
    let graphHeight = 400;
    if(Util.isMobile()){
        graphHeight = 300;
    }
    const yTickSpacing = 5;

    const xAxisFormatLabel = (value, index) => {
        if(index % 3 == 0){
            return Util.roundNice(value, 0) + " kg";
        }

        return "";
    }

	const graphData = []; // {label: 'Waste', value: 100, type: 'material' }, {label: 'Cardboard', value: 30, type: 'material' }];
	_.each( data, (d)=>{
		// graphData.push( {label: d.name, data: [{value: d.metric_tonnes * 1000, svg: {fill: d.main_colour}, label: d.name}], type: 'material', units: isUS ? 'lb' : 'kg' })
        const roundedTonnes = parseInt(Util.round(d.metric_tonnes * 1000, 0));
        if(roundedTonnes > maxValue.current){
            maxValue.current = roundedTonnes;
        }
        graphData.push( {label: d.name, value: roundedTonnes, svg: {fill: d.main_colour} })
	} );

    const legendChildren = (data, hoveredBand) => {
        if(hoveredBand == null){
            return null;
        }
        
        return <View style={{flexDirection: "row"}}>
            <View style={{height: 20, width: 20, backgroundColor: hoveredBand.svg.fill, marginRight: 10}}/> 
            <StrongText style={{marginRight: 10}}>{hoveredBand.label}</StrongText> 
            <StrongText>{Util.roundNice(hoveredBand.value, 0) + " kg"}</StrongText>
        </View>;
    }

	return <BarChart
        data={graphData}
        height={graphHeight}
        horizontal={true}
        showGrid={true}
        showLegend={true}
        legendChildren={(data, hoveredBand)=>legendChildren(data, hoveredBand)}
        xAxisData={ _.range(0, maxValue.current, 500) }
        xAccessor={ ({item}) => { return item.label; } }
        xAxisAccessor={ ({item}) => { return item; }}
        xAxisScale={scale.scaleLinear}
        xAxisFormatLabel={xAxisFormatLabel}
        yAccessor={ ({item}) => { return item.value; } }
        yAxisAccessor={ ({item}) => { return item.label; }}
        yAxisScale={scale.scaleBand}
        xAxisSVGProperties={{fontSize: 11, fill: 'black', fontFamily: 'Red Hat Display'}}
    />
}




export const DiversionSummary = ({data, portalData = undefined})=>{
    const navigation = useOpidNavigator();

    const filteredData = _.sortBy(data, ["name"]);

    let diversionTotalTonnes = 0;
    let totalTonnes = 0;

    let pieData = [];
    let miscDataRecycling = null;
    let miscDataWaste = null;

    const totalTonnage = _.reduce(filteredData, (sum, d)=>{
        return sum + d.metric_tonnes;
    }, 0);

    const cutOffPercent = 0.03;
    
    _.forEach(filteredData, (r) => {
        let fType = "wsc";
        let icon = r.icon;
        if(icon.indexOf(":") > -1){
            const splitIcon = icon.split(":");
            icon = splitIcon[1];
            fType = splitIcon[0];
        }

        if(r.epa_recycling == true){
            diversionTotalTonnes += r.metric_tonnes;
        }

        totalTonnes += r.metric_tonnes;

        if((r.metric_tonnes / totalTonnage) < cutOffPercent){
            //store in misc
            if(r.epa_recycling == true){
                if(miscDataRecycling == null){
                    miscDataRecycling = {id: "Miscellaneous Recycling", icon: icon, fType: fType, label: "", color: r.main_colour, value: r.metric_tonnes };
                } else {
                    miscDataRecycling.value += r.metric_tonnes;
                }
            } else {
                if(miscDataWaste == null){
                    miscDataWaste = {id: "Miscellaneous Waste", icon: icon, fType: fType, label: "", color: r.main_colour, value: r.metric_tonnes };
                } else {
                    miscDataWaste.value += r.metric_tonnes;
                }
            }
        } else {
            pieData.push({id: r.name, icon: icon, fType: fType, label: r.name, color: r.main_colour, value: r.metric_tonnes });
        }
    });

    if(miscDataRecycling != null){
        pieData.push(miscDataRecycling);
    }

    if(miscDataWaste != null){
        pieData.push(miscDataWaste);
    }

    let headingText = "Average Diversion";
    let length = null;

    if(portalData != undefined && portalData.siteFilter == null){
        length = "All";
    }

    if(filteredData.length > 0 && length == null){
        const grouped = _.groupBy(filteredData, "site_id");
        length = _.keys(grouped).length;

        if(grouped[undefined] != undefined){
            length--;
        }

        if(grouped[null] != undefined){
            length--;
        }
    }

    if(length != null){
        let plural = "";
        if(length == "All" || length > 1){
            plural = "s";
        }
        headingText += " (" + length + " Site" + plural + ")";
    }

    const diversionPercent = totalTonnes == 0? 0 : (diversionTotalTonnes/totalTonnes) * 100;
    
    return <View>
            <Heading3>{headingText}</Heading3>
            <CircleChart data={ pieData } units="MT" centeredMetric={Util.roundNice(diversionPercent, 0) + "%"} labelFontSize={14}></CircleChart>

    </View>
}


export default SummaryDashboard;
